import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isPostFailed } from '@mg/shared';
import { api } from '../lib/api';
import { useTenantKey } from '../lib/useTenantKey';
import { useEffectiveApprovalType } from '../lib/useEffectiveApprovalType';
import { useTenant } from '../auth/TenantContext';
import {
  ArrowLeft,
  CalendarDays,
  FileText,
  FolderKanban,
  Plus,
  Settings,
  SquarePen,
  Trash2,
} from 'lucide-react';
import { cn } from '../lib/cn';
import { hcFillSelected } from '../lib/forcedColors';
import { PageHeader } from '../components/shell/PageHeader';
import { Button } from '../components/ui/Button';
import { CampaignFormModal } from '../features/campaigns/CampaignFormModal';
import { CampaignsView } from '../features/campaigns/CampaignsView';
import { PostsView, type PostsSortByTab } from '../features/posts/PostsView';
import { CalendarView } from '../features/posts/CalendarView';
import { PostingScheduleView } from '../features/posts/PostingScheduleView';
import { LayoutToggle } from '../features/posts/LayoutToggle';
import { TrashView } from '../features/posts/TrashView';
import { postBadgeState } from '../features/posts/PostsView';
import {
  TestModeBanner,
  TestModeFailedPostsBanner,
} from '../features/testMode/TestModeBanner';
import { isTestModeFailure } from '../features/testMode/testMode';

type PageView = 'campaigns' | 'posts' | 'calendar' | 'scheduler' | 'trash';

function parsePageView(value: string | null): PageView {
  if (value === 'posts') return 'posts';
  if (value === 'calendar') return 'calendar';
  // The recurring posting schedule. NOT a tab — it's reached from the header link and takes over
  // the page, because it configures the queue rather than being another way to look at posts.
  if (value === 'scheduler') return 'scheduler';
  if (value === 'trash') return 'trash';
  return 'campaigns';
}

/**
 * Merged "Campaigns & Posts" hub. A top toggle switches between the two existing
 * surfaces — "Grouped by Campaign" (campaign cards + status tabs) and "All Posts"
 * (the flat, filterable post list) — persisted in the URL as `?view=`. The
 * per-view "New Campaign" / "New Content" action lives in the header. The campaign
 * detail page (/campaigns/:id) is unchanged and reached by drilling into a card.
 */
export function CampaignsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  const view = parsePageView(params.get('view'));

  // Failed posts are a workspace-level problem, so the banner is on the page rather than inside a
  // tab — you shouldn't have to be looking at the right view to find out a release didn't happen.
  // Shares the ['posts'] cache with both views, so this costs no extra fetch.
  const tenantKey = useTenantKey();
  const postsQuery = useQuery({
    queryKey: ['posts', tenantKey],
    queryFn: ({ signal }) => api.listPosts(signal),
    enabled: tenantKey !== '',
  });

  /**
   * The column picked on each Status tab of All Posts. Kept here rather than in PostsView: this page
   * stays mounted while its view changes, and Grouped, Calendar and Trash unmount PostsView, so a pick
   * survives a trip there and back. The picks belong to the workspace they were made in, and a switch
   * keeps this page mounted too, so they are dropped then: during render, so the other workspace's
   * list is never drawn with them.
   */
  const [postsSortByTab, setPostsSortByTab] = useState<PostsSortByTab>({});
  const [postsSortTenant, setPostsSortTenant] = useState(tenantKey);
  if (postsSortTenant !== tenantKey) {
    setPostsSortTenant(tenantKey);
    setPostsSortByTab({});
  }

  /**
   * Failed posts, split by WHY.
   *
   * A workspace in test mode has no release worker running for it, so a scheduled post never goes
   * out and eventually reads as failed with no error attached. Telling that person to "check your
   * post settings and connections" sends them to inspect a connection that is working perfectly —
   * so those get their own banner naming the actual cause. Anything else keeps the generic one,
   * and a workspace with both problems sees both rather than having one hidden by the other.
   */
  const approvalType = useEffectiveApprovalType();
  const { activeTenant } = useTenant();
  const failedPosts = (postsQuery.data ?? []).filter((p) => isPostFailed(p));
  const testModeFailedCount = failedPosts.filter((p) =>
    isTestModeFailure(p, postBadgeState(p, approvalType), activeTenant),
  ).length;
  const failedCount = failedPosts.length - testModeFailedCount;

  const setView = (next: PageView) => {
    const params2 = new URLSearchParams(params);
    params2.set('view', next);
    setParams(params2, { replace: true });
  };

  const openComposer = () => {
    const next = new URLSearchParams(params);
    next.set('compose', 'new');
    setParams(next, { replace: false });
  };

  const action = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => setView('scheduler')}
        className="focus-ring inline-flex items-center gap-1.5 rounded text-sm font-medium text-ink-muted hover:text-ink"
      >
        <Settings className="h-4 w-4" aria-hidden />
        Scheduler Settings
      </button>
      {view === 'campaigns' ? (
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          New Campaign
        </Button>
      ) : (
        <Button onClick={openComposer}>
          <SquarePen className="h-4 w-4" aria-hidden />
          New Post
        </Button>
      )}
    </div>
  );

  // The scheduler takes over the whole page — no tabs, no filters, just a way back.
  if (view === 'scheduler') {
    return (
      <>
        <Link
          to="/campaigns"
          className="focus-ring mb-3 inline-flex items-center gap-1 rounded text-sm font-medium text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          All campaigns
        </Link>
        <h1 className="mb-4 text-2xl font-bold text-ink">Scheduler Settings</h1>
        {/* Keyed by workspace: a switch keeps this page mounted, and the editor's draft (built once
            from the server) would otherwise stay the previous workspace's, and Save would write it
            into the new one. */}
        <PostingScheduleView key={tenantKey} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Campaigns & Posts"
        icon={FolderKanban}
        description="Group related posts under one shared message and approval chain, or browse every post across your campaigns."
        actions={action}
      />

      <TestModeBanner />

      <TestModeFailedPostsBanner count={testModeFailedCount} />

      {failedCount > 0 && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 dark:border-error-dark/30 bg-red-50 dark:bg-error-dark-tint px-4 py-3">
          <p className="text-sm text-red-800 dark:text-error-dark">
            You have {failedCount} failed post{failedCount === 1 ? '' : 's'}. Please check your post
            settings and connections, and then reschedule your post{failedCount === 1 ? '' : 's'}.
          </p>
          <Button
            variant="secondary"
            className="shrink-0"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('view', 'posts');
              next.set('tab', 'failed');
              setParams(next, { replace: true });
            }}
          >
            View Failed Posts
          </Button>
        </div>
      )}

      {/* Tabs left, List/Tile right, one row. The layout switcher is owned by the PAGE (rather
          than each view) so the two controls sit on the same line; Calendar has no layout choice,
          so the right slot is simply empty there and justify-between leaves the tabs in place. */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex items-center gap-1 rounded-lg border border-line-input bg-card p-0.5"
          role="tablist"
          aria-label="Campaigns & Posts view"
        >
          <ViewTab
            active={view === 'campaigns'}
            onClick={() => setView('campaigns')}
            icon={<FolderKanban className="h-4 w-4" aria-hidden />}
            label="Grouped by Campaign"
          />
          <ViewTab
            active={view === 'posts'}
            onClick={() => setView('posts')}
            icon={<FileText className="h-4 w-4" aria-hidden />}
            label="All Posts"
          />
          <ViewTab
            active={view === 'calendar'}
            onClick={() => setView('calendar')}
            icon={<CalendarDays className="h-4 w-4" aria-hidden />}
            label="Calendar"
          />
          <ViewTab
            active={view === 'trash'}
            onClick={() => setView('trash')}
            icon={<Trash2 className="h-4 w-4" aria-hidden />}
            label="Trash"
          />
        </div>

        {view === 'campaigns' ? (
          <LayoutToggle param="clayout" />
        ) : view === 'posts' ? (
          <LayoutToggle param="layout" />
        ) : null}
      </div>

      {view === 'campaigns' ? (
        <CampaignsView onNewCampaign={() => setCreating(true)} />
      ) : view === 'posts' ? (
        <PostsView sortByTab={postsSortByTab} onSortByTabChange={setPostsSortByTab} />
      ) : view === 'trash' ? (
        <TrashView />
      ) : (
        <CalendarView />
      )}

      <CampaignFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(c) => navigate(`/campaigns/${c.id}`)}
      />
    </>
  );
}

function ViewTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'focus-ring flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-tint text-tint-fg' : 'text-ink-muted hover:text-ink',
        active && hcFillSelected,
      )}
    >
      {icon}
      {label}
    </button>
  );
}
